import React, { useState, useEffect, useCallback } from 'react';
import { Capacitor } from '@capacitor/core';
import { PushNotifications } from '@capacitor/push-notifications';
import { callApi } from '../api/api';
import FirebaseImageUpload from '../components/FirebaseImageUpload';
import { messaging } from '../firebase';
import { getToken } from 'firebase/messaging';

const INDIAN_STATES = [
    'Andhra Pradesh','Arunachal Pradesh','Assam','Bihar','Chhattisgarh','Goa','Gujarat','Haryana',
    'Himachal Pradesh','Jharkhand','Karnataka','Kerala','Madhya Pradesh','Maharashtra','Manipur',
    'Meghalaya','Mizoram','Nagaland','Odisha','Punjab','Rajasthan','Sikkim','Tamil Nadu','Telangana',
    'Tripura','Uttar Pradesh','Uttarakhand','West Bengal','Delhi','Jammu & Kashmir','Ladakh',
    'Puducherry','Chandigarh','Andaman & Nicobar','Dadra & NH','Lakshadweep'
];

const getDistanceKm = (lat1, lng1, lat2, lng2) => {
    const R = 6371;
    const dLat = (lat2 - lat1) * Math.PI / 180;
    const dLng = (lng2 - lng1) * Math.PI / 180;
    const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
        Math.sin(dLng/2) * Math.sin(dLng/2);
    return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};

const DEFAULT_STORE = {
    name: 'Vimalaxmi Medical Store',
    address: 'Shop no. 737, Sector 15, Koperkhairane, Navi Mumbai - 400709',
    phone: '+919029122955',
    lat: 19.1124,
    lng: 73.0051
};

const ProfileView = ({ showMessage, isAuthenticated, showPage }) => {
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    // Edit Profile State
    const [isEditingProfile, setIsEditingProfile] = useState(false);
    const [editName, setEditName] = useState('');
    const [editEmail, setEditEmail] = useState('');
    const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);

    // Location State
    const [userLocation, setUserLocation] = useState('');
    const [userCoordinates, setUserCoordinates] = useState(null);
    const [distance, setDistance] = useState(null);
    const [duration, setDuration] = useState(null);
    const [isLocating, setIsLocating] = useState(false);

    // Add Address State
    const [isAddingAddress, setIsAddingAddress] = useState(false);
    const [newAddress, setNewAddress] = useState({
        label: 'Home', name: '', phone: '', line1: '', line2: '', city: '', state: '', pincode: ''
    });
    const [newAddressCoordinates, setNewAddressCoordinates] = useState(null);
    const [isDetectingAddress, setIsDetectingAddress] = useState(false);

    // Store State
    const [nearestStore, setNearestStore] = useState(DEFAULT_STORE);

    // Notification State
    const [notificationStatus, setNotificationStatus] = useState(
        typeof Notification !== 'undefined' ? Notification.permission : 'default'
    );

    useEffect(() => {
        if (Capacitor.isNativePlatform()) {
            PushNotifications.addListener('registration', async (token) => {
                try {
                    await callApi('/notifications/register-token', 'POST', { token: token.value });
                    showMessage('Push notifications enabled successfully!', 'success');
                    setNotificationStatus('granted');
                } catch (err) { console.error('Failed to save native push token', err); }
            });
            PushNotifications.addListener('registrationError', (error) => {
                console.error('Error on registration: ' + JSON.stringify(error));
            });
            PushNotifications.addListener('pushNotificationReceived', (notification) => {
                console.log('Push received: ', notification);
            });
        }
        return () => { if (Capacitor.isNativePlatform()) PushNotifications.removeAllListeners(); };
    }, []);

    const handleEnableNotifications = async () => {
        try {
            if (Capacitor.isNativePlatform()) {
                let permStatus = await PushNotifications.checkPermissions();
                if (permStatus.receive === 'prompt') permStatus = await PushNotifications.requestPermissions();
                if (permStatus.receive === 'granted') {
                    await PushNotifications.register();
                } else {
                    showMessage('You have blocked notifications. Please enable them in your device settings.', 'error');
                }
            } else {
                if (typeof Notification === 'undefined') {
                    showMessage('Push notifications are not supported on this device/browser.', 'error');
                    return;
                }
                const permission = await Notification.requestPermission();
                setNotificationStatus(permission);
                if (permission === 'granted') {
                    if (!messaging) return;
                    const vapidKey = process.env.REACT_APP_VAPID_KEY;
                    const currentToken = await getToken(messaging, vapidKey ? { vapidKey } : undefined);
                    if (currentToken) {
                        await callApi('/notifications/register-token', 'POST', { token: currentToken });
                        showMessage('Push notifications enabled successfully!', 'success');
                    }
                } else if (permission === 'denied') {
                    showMessage('You have blocked notifications. Please enable them in your browser settings.', 'error');
                }
            }
        } catch (error) {
            showMessage(`Failed: ${error.message || 'Unknown error'}`, 'error');
        }
    };

    useEffect(() => {
        const fetchAndFindNearest = async () => {
            try {
                const res = await callApi('/stores');
                if (res.success && res.stores && res.stores.length > 0) {
                    if (userCoordinates && userCoordinates.lat && userCoordinates.lng) {
                        let nearest = res.stores[0];
                        let minDist = Infinity;
                        res.stores.forEach(store => {
                            const dist = getDistanceKm(userCoordinates.lat, userCoordinates.lng, store.lat, store.lng);
                            if (dist < minDist) { minDist = dist; nearest = store; }
                        });
                        setNearestStore(nearest);
                    }
                    // If no user coordinates, keep DEFAULT_STORE (Vimalaxmi Medical Store)
                }
            } catch (e) { /* fallback to default */ }
        };
        fetchAndFindNearest();
    }, [userCoordinates]);

    useEffect(() => {
        if (!isAuthenticated) { showPage('login'); return; }
        const fetchProfile = async () => {
            try {
                const response = await callApi('/users/profile', 'GET');
                if (response.success) {
                    setUser(response.user);
                    setEditName(response.user.name || '');
                    setEditEmail(response.user.email || '');
                } else {
                    showMessage('Failed to load profile', 'error');
                }
            } catch (err) {
                if (err.message && (err.message.toLowerCase().includes('not authorized') || err.message.includes('401'))) {
                    showMessage('Your session has expired. Please log in again.', 'error');
                    showPage('logout');
                } else {
                    showMessage('Server error loading profile. Please try again later.', 'error');
                }
            } finally {
                setLoading(false);
            }
        };
        fetchProfile();
    }, [isAuthenticated, showPage, showMessage]);

    const handleSaveProfile = async () => {
        try {
            const response = await callApi('/users/profile', 'PUT', { name: editName, email: editEmail });
            if (response.success) {
                setUser(response.user);
                localStorage.setItem('userInfo', JSON.stringify(response.user));
                setIsEditingProfile(false);
                showMessage('Profile updated successfully!', 'success');
            } else {
                showMessage(response.message || 'Failed to update profile', 'error');
            }
        } catch (err) { showMessage('Server error updating profile', 'error'); }
    };

    const handleAddAddress = async () => {
        if (!newAddress.line1 || !newAddress.city || !newAddress.state || !newAddress.pincode) {
            showMessage('Please fill all required address fields', 'error');
            return;
        }
        try {
            const updatedAddresses = [...(user.addresses || []), newAddress];
            const response = await callApi('/users/profile', 'PUT', { addresses: updatedAddresses });
            if (response.success) {
                setUser(response.user);
                localStorage.setItem('userInfo', JSON.stringify(response.user));
                setIsAddingAddress(false);
                setNewAddress({ label: 'Home', name: '', phone: '', line1: '', line2: '', city: '', state: '', pincode: '' });
                showMessage('Address added successfully!', 'success');
            } else {
                showMessage(response.message || 'Failed to add address', 'error');
            }
        } catch (err) { showMessage('Server error adding address', 'error'); }
    };

    const handleDeleteAddress = async (index) => {
        if (!window.confirm('Are you sure you want to delete this address?')) return;
        try {
            const updatedAddresses = [...user.addresses];
            updatedAddresses.splice(index, 1);
            const response = await callApi('/users/profile', 'PUT', { addresses: updatedAddresses });
            if (response.success) {
                setUser(response.user);
                localStorage.setItem('userInfo', JSON.stringify(response.user));
                showMessage('Address deleted', 'success');
            } else {
                showMessage('Failed to delete address', 'error');
            }
        } catch (err) { showMessage('Server error deleting address', 'error'); }
    };

    const handleUploadSuccess = async (url) => {
        setIsUploadingPhoto(false);
        try {
            const response = await callApi('/users/profile', 'PUT', { profilePic: url });
            if (response.success) {
                setUser(response.user);
                localStorage.setItem('userInfo', JSON.stringify(response.user));
                showMessage('Profile photo updated', 'success');
            } else {
                showMessage('Failed to update profile photo', 'error');
            }
        } catch (err) { showMessage('Server error updating profile photo', 'error'); }
    };

    const handleAutoDetectAddressForm = () => {
        if (!navigator.geolocation) { showMessage("Geolocation is not supported by your browser.", "error"); return; }
        setIsDetectingAddress(true);
        navigator.geolocation.getCurrentPosition(async (position) => {
            const { latitude, longitude } = position.coords;
            setNewAddressCoordinates({ lat: latitude, lng: longitude });
            if (!window.google || !window.google.maps) { setIsDetectingAddress(false); return; }
            try {
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
                    if (status === "OK" && results[0]) {
                        let area = "", pincode = "", city = "", stateName = "", street = "";
                        for (let component of results[0].address_components) {
                            if (component.types.includes("sublocality") || component.types.includes("locality")) { if (!area) area = component.long_name; }
                            if (component.types.includes("postal_code")) pincode = component.long_name;
                            if (component.types.includes("locality") || component.types.includes("administrative_area_level_2")) { if (!city) city = component.long_name; }
                            if (component.types.includes("administrative_area_level_1")) stateName = component.long_name;
                            if (component.types.includes("route")) street = component.long_name;
                        }
                        setNewAddress(prev => ({ ...prev, line1: street || prev.line1, line2: area || prev.line2, city: city || prev.city, state: stateName || prev.state, pincode: pincode || prev.pincode }));
                    }
                    setIsDetectingAddress(false);
                });
            } catch (error) { setIsDetectingAddress(false); }
        }, () => { setIsDetectingAddress(false); showMessage("Permission denied or location unavailable.", "error"); }, { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
    };

    const calculateDistance = useCallback((lat, lng) => {
        if (!window.google || !window.google.maps) {
            const storeLat = 19.0790, storeLng = 73.0030, R = 6371;
            const dLat = (lat - storeLat) * Math.PI / 180;
            const dLng = (lng - storeLng) * Math.PI / 180;
            const a = Math.sin(dLat/2)**2 + Math.cos(storeLat * Math.PI/180) * Math.cos(lat * Math.PI/180) * Math.sin(dLng/2)**2;
            setDistance((R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a))).toFixed(1));
            return;
        }
        const service = new window.google.maps.DistanceMatrixService();
        service.getDistanceMatrix({
            origins: [new window.google.maps.LatLng(lat, lng)],
            destinations: [new window.google.maps.LatLng(19.100378, 73.0015257)],
            travelMode: 'TWO_WHEELER',
        }, (response, status) => {
            if (status === 'OK' && response.rows[0].elements[0].status === 'OK') {
                setDistance(response.rows[0].elements[0].distance.text.replace(' km', ''));
                setDuration(response.rows[0].elements[0].duration.text);
            }
        });
    }, []);

    useEffect(() => {
        if (userCoordinates) calculateDistance(userCoordinates.lat, userCoordinates.lng);
        else setDistance(null);
    }, [userCoordinates, calculateDistance]);

    const handleAutoLocate = () => {
        if (!navigator.geolocation) { showMessage("Geolocation is not supported by your browser.", "error"); return; }
        setIsLocating(true);
        navigator.geolocation.getCurrentPosition(async (position) => {
            const { latitude, longitude } = position.coords;
            setUserCoordinates({ lat: latitude, lng: longitude });
            if (!window.google || !window.google.maps) { setUserLocation("Detected Location"); setIsLocating(false); return; }
            try {
                const geocoder = new window.google.maps.Geocoder();
                geocoder.geocode({ location: { lat: latitude, lng: longitude } }, (results, status) => {
                    if (status === "OK" && results[0]) {
                        const parts = results[0].formatted_address.split(',');
                        const areaStr = parts.slice(0, 3).join(',').trim();
                        let pincode = "";
                        for (let component of results[0].address_components) {
                            if (component.types.includes("postal_code")) pincode = component.long_name;
                        }
                        setUserLocation([areaStr || "Unknown Area", pincode].filter(Boolean).join(" - "));
                    } else { setUserLocation("Location Found"); }
                    setIsLocating(false);
                });
            } catch (error) {
                showMessage("Could not detect your detailed location.", "error");
                setUserLocation("Location Found");
                setIsLocating(false);
            }
        }, () => { setIsLocating(false); showMessage("Permission denied or location unavailable.", "error"); },
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 });
    };

    if (loading) {
        return (
            <div className="flex flex-col justify-center items-center h-64 gap-4">
                <div className="animate-spin rounded-full h-14 w-14 border-t-4 border-b-4 border-sky-500"></div>
                <p className="text-slate-400 text-sm font-medium animate-pulse">Loading your profile...</p>
            </div>
        );
    }

    if (!user) return null;

    const initials = (user.name || 'U').split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

    return (
        <div className="max-w-2xl mx-auto px-3 sm:px-4 pb-10 space-y-4 sm:space-y-6 animate-fade-in">

            {/* ── Profile Header Card ── */}
            <div className="bg-gradient-to-br from-sky-500 via-sky-600 to-indigo-600 rounded-2xl sm:rounded-3xl shadow-xl overflow-hidden">
                {/* Top bar: avatar + logout */}
                <div className="px-4 sm:px-6 pt-5 sm:pt-7 pb-4 flex items-start justify-between gap-3">
                    <div className="flex items-center gap-3 sm:gap-4 min-w-0">
                        {/* Avatar */}
                        <div className="relative shrink-0">
                            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white/20 rounded-2xl flex items-center justify-center text-2xl sm:text-3xl font-extrabold text-white shadow-lg border-2 border-white/30 overflow-hidden">
                                {user.profilePic ? (
                                    <img src={user.profilePic} alt="Profile" className="w-full h-full object-cover" />
                                ) : initials}
                            </div>
                            <button
                                onClick={() => setIsUploadingPhoto(!isUploadingPhoto)}
                                className="absolute -bottom-1 -right-1 bg-white text-sky-600 w-7 h-7 rounded-xl flex items-center justify-center shadow-md hover:bg-sky-50 transition-colors border border-sky-100"
                                title="Change photo"
                            >
                                <i className="fas fa-camera text-xs"></i>
                            </button>
                        </div>
                        {/* Name & Details */}
                        <div className="text-white min-w-0">
                            <h2 className="text-xl sm:text-2xl font-extrabold leading-tight truncate">{user.name || 'Your Profile'}</h2>
                            <p className="text-sky-100 text-sm mt-0.5 flex items-center gap-1.5 truncate">
                                <i className="fas fa-phone text-xs opacity-70"></i>
                                <span className="font-mono font-semibold">{user.mobile}</span>
                            </p>
                            {user.email && (
                                <p className="text-sky-100 text-xs mt-0.5 flex items-center gap-1.5 truncate">
                                    <i className="fas fa-envelope text-xs opacity-70"></i>
                                    <span className="truncate">{user.email}</span>
                                </p>
                            )}
                        </div>
                    </div>
                    <button
                        onClick={() => showPage('logout')}
                        className="shrink-0 bg-white/20 hover:bg-white/30 text-white px-3 py-2 rounded-xl font-bold text-xs sm:text-sm transition-all flex items-center gap-1.5 border border-white/20 backdrop-blur-sm"
                    >
                        <i className="fas fa-sign-out-alt text-xs"></i>
                        <span className="hidden sm:inline">Logout</span>
                    </button>
                </div>

                {/* Wallet Balance Strip */}
                <div className="mx-4 sm:mx-6 mb-5 bg-white/15 backdrop-blur-sm rounded-2xl px-4 py-3 flex items-center justify-between border border-white/20">
                    <div className="flex items-center gap-2 text-white">
                        <div className="w-8 h-8 bg-white/20 rounded-xl flex items-center justify-center">
                            <i className="fas fa-wallet text-sm"></i>
                        </div>
                        <div>
                            <p className="text-xs text-sky-200 font-medium">Wallet Balance</p>
                            <p className="text-lg font-extrabold leading-none">₹{(user.walletBalance || 0).toFixed(2)}</p>
                        </div>
                    </div>
                    <span className="text-xs text-sky-200 bg-white/10 px-2.5 py-1 rounded-full font-semibold border border-white/10">
                        QwickMed Cash
                    </span>
                </div>

                {/* Photo Upload Panel */}
                {isUploadingPhoto && (
                    <div className="mx-4 sm:mx-6 mb-5 bg-white rounded-2xl p-4 shadow-inner border border-slate-100">
                        <div className="flex justify-between items-center mb-3">
                            <h4 className="text-sm font-bold text-slate-700">Update Profile Photo</h4>
                            <button onClick={() => setIsUploadingPhoto(false)} className="text-slate-400 hover:text-slate-600 w-7 h-7 flex items-center justify-center rounded-full hover:bg-slate-100">
                                <i className="fas fa-times text-xs"></i>
                            </button>
                        </div>
                        <FirebaseImageUpload onUploadSuccess={handleUploadSuccess} folder="profiles" />
                    </div>
                )}
            </div>

            {/* ── Personal Details Card ── */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="px-4 sm:px-6 py-4 border-b border-slate-50 flex justify-between items-center">
                    <h3 className="text-base sm:text-lg font-extrabold text-slate-800 flex items-center gap-2">
                        <span className="w-7 h-7 bg-sky-50 text-sky-600 rounded-lg flex items-center justify-center text-xs">
                            <i className="fas fa-user"></i>
                        </span>
                        Personal Details
                    </h3>
                    {!isEditingProfile && (
                        <button
                            onClick={() => setIsEditingProfile(true)}
                            className="text-sky-600 hover:text-sky-800 font-bold bg-sky-50 hover:bg-sky-100 px-3 py-1.5 rounded-xl transition-colors text-xs flex items-center gap-1.5"
                        >
                            <i className="fas fa-pen text-[10px]"></i> Edit
                        </button>
                    )}
                </div>

                <div className="p-4 sm:p-6">
                    {isEditingProfile ? (
                        <div className="space-y-3">
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Full Name</label>
                                <input
                                    type="text"
                                    value={editName}
                                    onChange={(e) => setEditName(e.target.value)}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-sky-500 rounded-xl outline-none transition-all font-semibold text-slate-800"
                                    placeholder="Your full name"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Email Address</label>
                                <input
                                    type="email"
                                    value={editEmail}
                                    onChange={(e) => setEditEmail(e.target.value)}
                                    className="w-full px-4 py-3 bg-slate-50 border border-slate-200 focus:bg-white focus:border-sky-500 rounded-xl outline-none transition-all font-semibold text-slate-800"
                                    placeholder="you@example.com"
                                />
                            </div>
                            <div className="flex gap-3 pt-2">
                                <button
                                    onClick={handleSaveProfile}
                                    className="flex-1 py-3 bg-sky-500 hover:bg-sky-600 text-white font-bold rounded-xl shadow-md shadow-sky-500/20 transition-all flex items-center justify-center gap-2"
                                >
                                    <i className="fas fa-check text-sm"></i> Save Changes
                                </button>
                                <button
                                    onClick={() => setIsEditingProfile(false)}
                                    className="py-3 px-5 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-100">
                                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold mb-1">Full Name</p>
                                <p className="text-sm font-bold text-slate-800">{user.name || <span className="text-slate-400 italic">Not provided</span>}</p>
                            </div>
                            <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-100">
                                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold mb-1">Mobile</p>
                                <p className="text-sm font-bold text-slate-800 font-mono">{user.mobile}</p>
                            </div>
                            <div className="bg-slate-50 rounded-xl p-3.5 border border-slate-100 sm:col-span-2">
                                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold mb-1">Email</p>
                                <p className="text-sm font-bold text-slate-800 break-all">{user.email || <span className="text-slate-400 italic">Not provided</span>}</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Saved Addresses Card ── */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="px-4 sm:px-6 py-4 border-b border-slate-50 flex justify-between items-center gap-3">
                    <h3 className="text-base sm:text-lg font-extrabold text-slate-800 flex items-center gap-2">
                        <span className="w-7 h-7 bg-emerald-50 text-emerald-600 rounded-lg flex items-center justify-center text-xs">
                            <i className="fas fa-map-marker-alt"></i>
                        </span>
                        Saved Addresses
                    </h3>
                    <button
                        onClick={() => setIsAddingAddress(!isAddingAddress)}
                        className={`shrink-0 font-bold text-xs px-3 py-2 rounded-xl transition-all flex items-center gap-1.5 ${
                            isAddingAddress
                                ? 'bg-red-50 text-red-600 hover:bg-red-100 border border-red-100'
                                : 'bg-emerald-500 hover:bg-emerald-600 text-white shadow-md shadow-emerald-500/20'
                        }`}
                    >
                        <i className={`fas ${isAddingAddress ? 'fa-times' : 'fa-plus'} text-[10px]`}></i>
                        {isAddingAddress ? 'Cancel' : 'Add New'}
                    </button>
                </div>

                {/* Add Address Form */}
                {isAddingAddress && (
                    <div className="p-4 sm:p-6 border-b border-slate-50 bg-slate-50/50 animate-fade-in">
                        <div className="flex justify-between items-center mb-4">
                            <h4 className="font-bold text-slate-700 text-sm">New Delivery Address</h4>
                            <button
                                onClick={handleAutoDetectAddressForm}
                                disabled={isDetectingAddress}
                                className="bg-sky-50 hover:bg-sky-100 text-sky-600 border border-sky-200 px-3 py-2 rounded-xl font-bold text-xs transition-colors flex items-center gap-1.5 disabled:opacity-60"
                            >
                                {isDetectingAddress ? <i className="fas fa-spinner fa-spin"></i> : <i className="fas fa-location-arrow text-[10px]"></i>}
                                {isDetectingAddress ? 'Detecting...' : 'Auto Fill'}
                            </button>
                        </div>

                        {newAddressCoordinates && (
                            <div className="w-full h-40 sm:h-48 rounded-xl overflow-hidden border border-slate-200 shadow-inner mb-4">
                                <iframe
                                    title="Address Map Preview"
                                    width="100%"
                                    height="100%"
                                    style={{ border: 0 }}
                                    src={`https://www.google.com/maps/embed/v1/place?key=AIzaSyA6t8plGGMYeSpUH-l042UCKSfd1WrGsao&q=${newAddressCoordinates.lat},${newAddressCoordinates.lng}&zoom=16`}
                                    allowFullScreen
                                ></iframe>
                            </div>
                        )}

                        {/* Label chooser */}
                        <div className="mb-4">
                            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-2">Save As</label>
                            <div className="flex gap-2">
                                {['Home', 'Office', 'Other'].map(type => (
                                    <button
                                        key={type}
                                        onClick={() => setNewAddress({...newAddress, label: type})}
                                        className={`flex-1 py-2 rounded-xl font-bold text-xs transition-all ${
                                            newAddress.label === type
                                                ? 'bg-sky-500 text-white shadow-md shadow-sky-500/20'
                                                : 'bg-white border border-slate-200 text-slate-600 hover:bg-slate-50'
                                        }`}
                                    >
                                        <i className={`fas ${type === 'Home' ? 'fa-home' : type === 'Office' ? 'fa-building' : 'fa-map-pin'} mr-1.5`}></i>
                                        {type}
                                    </button>
                                ))}
                            </div>
                        </div>

                        <div className="space-y-3">
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-1.5">Recipient Name</label>
                                    <input type="text" value={newAddress.name} onChange={(e) => setNewAddress({...newAddress, name: e.target.value})} placeholder="Full name" className="w-full px-3 py-2.5 bg-white border border-slate-200 focus:border-sky-500 rounded-xl outline-none transition-all text-sm font-medium" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-1.5">Recipient Phone</label>
                                    <input type="text" value={newAddress.phone} onChange={(e) => setNewAddress({...newAddress, phone: e.target.value})} placeholder="+91 XXXXX XXXXX" className="w-full px-3 py-2.5 bg-white border border-slate-200 focus:border-sky-500 rounded-xl outline-none transition-all text-sm font-medium" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1.5">Address Line 1 <span className="text-red-400">*</span></label>
                                <input type="text" value={newAddress.line1} onChange={(e) => setNewAddress({...newAddress, line1: e.target.value})} placeholder="Flat/House No., Building, Street" className="w-full px-3 py-2.5 bg-white border border-slate-200 focus:border-sky-500 rounded-xl outline-none transition-all text-sm font-medium" />
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1.5">Address Line 2</label>
                                <input type="text" value={newAddress.line2} onChange={(e) => setNewAddress({...newAddress, line2: e.target.value})} placeholder="Area, Landmark (optional)" className="w-full px-3 py-2.5 bg-white border border-slate-200 focus:border-sky-500 rounded-xl outline-none transition-all text-sm font-medium" />
                            </div>
                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-1.5">Pincode <span className="text-red-400">*</span></label>
                                    <input type="text" value={newAddress.pincode} onChange={(e) => setNewAddress({...newAddress, pincode: e.target.value.replace(/\D/g,'').slice(0,6)})} maxLength="6" placeholder="6-digit" className="w-full px-3 py-2.5 bg-white border border-slate-200 focus:border-sky-500 rounded-xl outline-none transition-all text-sm font-medium font-mono" />
                                </div>
                                <div>
                                    <label className="block text-xs font-bold text-slate-500 mb-1.5">City <span className="text-red-400">*</span></label>
                                    <input type="text" value={newAddress.city} onChange={(e) => setNewAddress({...newAddress, city: e.target.value})} placeholder="City" className="w-full px-3 py-2.5 bg-white border border-slate-200 focus:border-sky-500 rounded-xl outline-none transition-all text-sm font-medium" />
                                </div>
                            </div>
                            <div>
                                <label className="block text-xs font-bold text-slate-500 mb-1.5">State <span className="text-red-400">*</span></label>
                                <select value={newAddress.state} onChange={(e) => setNewAddress({...newAddress, state: e.target.value})} className="w-full px-3 py-2.5 bg-white border border-slate-200 focus:border-sky-500 rounded-xl outline-none transition-all text-sm font-medium cursor-pointer">
                                    <option value="">Select State</option>
                                    {INDIAN_STATES.map(state => <option key={state} value={state}>{state}</option>)}
                                </select>
                            </div>
                            <button
                                onClick={handleAddAddress}
                                className="w-full py-3 bg-emerald-500 hover:bg-emerald-600 text-white font-bold rounded-xl shadow-md shadow-emerald-500/20 transition-all flex items-center justify-center gap-2 mt-2"
                            >
                                <i className="fas fa-map-marker-alt text-sm"></i> Save Address
                            </button>
                        </div>
                    </div>
                )}

                {/* Address List */}
                <div className="p-4 sm:p-6">
                    {!user.addresses || user.addresses.length === 0 ? (
                        <div className="py-10 text-center border-2 border-dashed border-slate-200 rounded-xl">
                            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <i className="fas fa-map-marker-alt text-xl text-slate-300"></i>
                            </div>
                            <p className="text-slate-500 font-semibold text-sm">No saved addresses yet</p>
                            <p className="text-xs text-slate-400 mt-1">Add one to make checkout faster!</p>
                        </div>
                    ) : (
                        <div className="space-y-3">
                            {user.addresses.map((addr, idx) => (
                                <div key={idx} className="relative bg-slate-50 border border-slate-100 rounded-2xl p-4 hover:border-sky-200 hover:bg-sky-50/30 transition-all">
                                    <div className="flex items-start justify-between gap-2">
                                        <div className="flex items-center gap-2 mb-2">
                                            <span className={`inline-flex items-center gap-1.5 text-xs font-extrabold px-2.5 py-1 rounded-full uppercase tracking-wider ${
                                                addr.label === 'Home' ? 'bg-sky-100 text-sky-700' :
                                                addr.label === 'Office' ? 'bg-purple-100 text-purple-700' :
                                                'bg-slate-200 text-slate-700'
                                            }`}>
                                                <i className={`fas ${addr.label === 'Home' ? 'fa-home' : addr.label === 'Office' ? 'fa-building' : 'fa-map-pin'} text-[9px]`}></i>
                                                {addr.label}
                                            </span>
                                        </div>
                                        <button
                                            onClick={() => handleDeleteAddress(idx)}
                                            className="shrink-0 w-7 h-7 rounded-xl bg-red-50 text-red-400 hover:bg-red-500 hover:text-white flex items-center justify-center transition-colors"
                                        >
                                            <i className="fas fa-trash-alt text-xs"></i>
                                        </button>
                                    </div>
                                    <p className="font-bold text-slate-800 text-sm">{addr.name || user.name}</p>
                                    {addr.phone && <p className="text-slate-500 text-xs mt-0.5"><i className="fas fa-phone-alt text-[10px] mr-1 opacity-50"></i>{addr.phone}</p>}
                                    <div className="text-slate-600 text-xs mt-2 leading-relaxed space-y-0.5">
                                        <p>{addr.line1}</p>
                                        {addr.line2 && <p>{addr.line2}</p>}
                                        <p className="font-semibold text-slate-700">{addr.city}, {addr.state} — <span className="font-mono">{addr.pincode}</span></p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>

            {/* ── Delivery Location Card ── */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="px-4 sm:px-6 py-4 border-b border-slate-50 flex justify-between items-center gap-3">
                    <h3 className="text-base sm:text-lg font-extrabold text-slate-800 flex items-center gap-2">
                        <span className="w-7 h-7 bg-amber-50 text-amber-500 rounded-lg flex items-center justify-center text-xs">
                            <i className="fas fa-map-marked-alt"></i>
                        </span>
                        Delivery Location
                    </h3>
                    <button
                        onClick={handleAutoLocate}
                        disabled={isLocating}
                        className="shrink-0 bg-emerald-50 hover:bg-emerald-100 text-emerald-600 border border-emerald-200 px-3 py-2 rounded-xl font-bold text-xs transition-colors flex items-center gap-1.5 disabled:opacity-60"
                    >
                        {isLocating ? <i className="fas fa-spinner fa-spin text-[10px]"></i> : <i className="fas fa-crosshairs text-[10px]"></i>}
                        {isLocating ? 'Detecting...' : 'Auto Detect'}
                    </button>
                </div>

                <div className="p-4 sm:p-6">
                    {userLocation ? (
                        <div className="space-y-3">
                            {userCoordinates && (
                                <div className="w-full h-48 sm:h-56 rounded-xl overflow-hidden border border-slate-200 shadow-inner">
                                    <iframe
                                        title="Live Driving Route"
                                        width="100%"
                                        height="100%"
                                        style={{ border: 0 }}
                                        src={`https://www.google.com/maps/embed/v1/directions?key=AIzaSyA6t8plGGMYeSpUH-l042UCKSfd1WrGsao&origin=${userCoordinates.lat},${userCoordinates.lng}&destination=19.100378,73.0015257&mode=driving`}
                                        allowFullScreen
                                    ></iframe>
                                </div>
                            )}
                            <div className="bg-slate-50 rounded-xl border border-slate-100 p-3.5">
                                <p className="text-[10px] text-slate-400 uppercase tracking-wider font-bold mb-1">Detected Area</p>
                                <p className="text-sm font-bold text-slate-800">{userLocation.split(' - ')[0]}</p>
                                {userLocation.includes(' - ') && (
                                    <p className="text-xs text-slate-500 font-medium mt-0.5">Pincode: <span className="font-mono font-bold text-slate-700">{userLocation.split(' - ')[1]}</span></p>
                                )}
                            </div>
                            {(distance || duration) && (
                                <div className="grid grid-cols-2 gap-3">
                                    {distance && (
                                        <div className="bg-sky-50 border border-sky-100 rounded-xl p-3 text-center">
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-sky-500 mb-1">Distance</p>
                                            <p className="text-2xl font-extrabold text-sky-700">{distance} <span className="text-sm font-bold">km</span></p>
                                        </div>
                                    )}
                                    {duration && (
                                        <div className="bg-amber-50 border border-amber-100 rounded-xl p-3 text-center">
                                            <p className="text-[10px] font-bold uppercase tracking-wider text-amber-500 mb-1">Travel Time</p>
                                            <p className="text-xl font-extrabold text-amber-700">{duration}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    ) : (
                        <div className="py-10 text-center border-2 border-dashed border-slate-200 rounded-xl">
                            <div className="w-12 h-12 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-3">
                                <i className="fas fa-location-arrow text-xl text-slate-300"></i>
                            </div>
                            <p className="text-slate-500 font-semibold text-sm">Location not detected</p>
                            <p className="text-xs text-slate-400 mt-1">Tap "Auto Detect" to find your distance from our store.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* ── Notification Settings Card ── */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
                <div className="px-4 sm:px-6 py-4 border-b border-slate-50">
                    <h3 className="text-base sm:text-lg font-extrabold text-slate-800 flex items-center gap-2">
                        <span className="w-7 h-7 bg-violet-50 text-violet-600 rounded-lg flex items-center justify-center text-xs">
                            <i className="fas fa-bell"></i>
                        </span>
                        Notification Settings
                    </h3>
                </div>
                <div className="p-4 sm:p-6">
                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between bg-slate-50 rounded-xl border border-slate-100 p-4">
                        <div className="flex-1 min-w-0">
                            <p className="text-sm font-bold text-slate-800">Push Notifications</p>
                            <p className="text-xs text-slate-500 mt-0.5 leading-relaxed">Get alerts for refills, offers & delivery updates on your device.</p>
                            <div className="mt-2 flex items-center gap-2">
                                <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">Status:</span>
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                                    notificationStatus === 'granted' ? 'bg-emerald-100 text-emerald-700' :
                                    notificationStatus === 'denied' ? 'bg-red-100 text-red-700' :
                                    'bg-slate-200 text-slate-600'
                                }`}>
                                    {notificationStatus === 'granted' ? '✓ Enabled' : notificationStatus === 'denied' ? '✗ Blocked' : 'Not Set Up'}
                                </span>
                            </div>
                        </div>
                        {notificationStatus !== 'granted' && (
                            <button
                                onClick={handleEnableNotifications}
                                className="shrink-0 w-full sm:w-auto bg-violet-600 hover:bg-violet-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 shadow-md shadow-violet-500/20"
                            >
                                <i className="fas fa-bell text-sm"></i>
                                Enable Notifications
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {/* ── Store Info Footer (mobile) ── */}
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-4 sm:p-6">
                <h4 className="text-xs font-bold text-slate-400 uppercase tracking-wider mb-3 flex items-center gap-2">
                    <i className="fas fa-store text-sky-400"></i> Nearest Store
                </h4>
                <p className="text-sm font-bold text-slate-800">{nearestStore.name}</p>
                <p className="text-xs text-slate-500 mt-1 leading-relaxed">{nearestStore.address}</p>
                <a
                    href={`tel:${nearestStore.phone}`}
                    className="mt-3 inline-flex items-center gap-2 bg-sky-50 hover:bg-sky-100 text-sky-700 border border-sky-100 px-4 py-2 rounded-xl font-bold text-xs transition-colors"
                >
                    <i className="fas fa-phone-alt text-[10px]"></i>
                    {nearestStore.phone}
                </a>
                <div className="mt-4 pt-4 border-t border-slate-50 text-center">
                    <p className="text-xs text-slate-400">&copy; {new Date().getFullYear()} QwickMed. All rights reserved.</p>
                    <p className="text-[10px] text-slate-300 mt-0.5">A brand of Caretica World Private Limited.</p>
                </div>
            </div>

        </div>
    );
};

export default ProfileView;
